import type {
  AndCriterionInput,
  OrCriterionInput,
  NotCriterionInput,
  EqualsCriterion,
  ContainsCriterion,
  GreaterThanCriterion,
  GreaterEqualThanCriterion,
  LessThanCriterion,
  LessEqualThanCriterion,
  CurrencyAmountCriterion,
  EntityCriterion,
  CalendarEventCriterion,
  ContractualPartyCriterion,
  MatterCriterion,
  OwnerCriterion,
  DateCriterion,
  ListAnyCriterion,
  ListAllCriterion,
} from '@/api';
import { isEnumValue } from '@/utils/enumUtils';

/**
 * Constants for criterion types used throughout the filter system
 * These constants are extracted from API types to ensure type safety and avoid hardcoding
 */

/**
 * Enum for criterion type literals from API types
 * These are the actual values used in the API
 */
export enum CRITERION_TYPES {
  // Logical operators
  AND = 'and',
  OR = 'or',
  NOT = 'not',

  // Comparison operators
  EQUALS = '==',
  CONTAINS = 'in',
  GREATER_THAN = '>',
  GREATER_EQUAL = '>=',
  LESS_THAN = '<',
  LESS_EQUAL = '<=',

  // Special criterion types
  CURRENCY_AMOUNT = 'currency_amount',
  ENTITY = 'entity',
  CALENDAR_EVENT = 'calendar_event',

  // List criteria
  LIST_ANY = 'list_any',
  LIST_ALL = 'list_all',

  // Static column types
  CONTRACTUAL_PARTY = 'contractual_party',
  MATTER = 'matter',
  OWNER = 'owner',
  DATE = 'date',
}

/**
 * Type for all criterion type values
 */
export type CriterionTypeValue = CRITERION_TYPES;

/**
 * Unified type for any criterion item
 */
export type CriterionItem =
  | AndCriterionInput
  | OrCriterionInput
  | NotCriterionInput
  | EqualsCriterion
  | ContainsCriterion
  | GreaterThanCriterion
  | GreaterEqualThanCriterion
  | LessThanCriterion
  | LessEqualThanCriterion
  | CurrencyAmountCriterion
  | EntityCriterion
  | CalendarEventCriterion
  | ContractualPartyCriterion
  | MatterCriterion
  | OwnerCriterion
  | DateCriterion
  | ListAnyCriterion
  | ListAllCriterion;

/**
 * Type for logical operator criterion types
 */
export type LogicalOperatorType = CRITERION_TYPES.AND | CRITERION_TYPES.OR | CRITERION_TYPES.NOT;

/**
 * Type for list operator criterion types
 */
export type ListOperatorType = CRITERION_TYPES.LIST_ANY | CRITERION_TYPES.LIST_ALL;

/**
 * Type for comparison operator criterion types
 */
export type ComparisonOperatorType =
  | CRITERION_TYPES.EQUALS
  | CRITERION_TYPES.CONTAINS
  | CRITERION_TYPES.GREATER_THAN
  | CRITERION_TYPES.GREATER_EQUAL
  | CRITERION_TYPES.LESS_THAN
  | CRITERION_TYPES.LESS_EQUAL;

/**
 * Type for special criterion types
 */
export type SpecialCriterionType =
  CRITERION_TYPES.CURRENCY_AMOUNT | CRITERION_TYPES.ENTITY | CRITERION_TYPES.CALENDAR_EVENT;

/**
 * Type for static column criterion types
 */
export type StaticColumnType =
  | CRITERION_TYPES.CONTRACTUAL_PARTY
  | CRITERION_TYPES.MATTER
  | CRITERION_TYPES.OWNER
  | CRITERION_TYPES.DATE;

/**
 * Check if a value is a logical operator type
 */
export function isLogicalOperatorType(value: unknown): value is LogicalOperatorType {
  return (
    value === CRITERION_TYPES.AND || value === CRITERION_TYPES.OR || value === CRITERION_TYPES.NOT
  );
}

/**
 * Check if a value is a list operator type
 */
export function isListOperatorType(value: unknown): value is ListOperatorType {
  return value === CRITERION_TYPES.LIST_ANY || value === CRITERION_TYPES.LIST_ALL;
}

/**
 * Check if a value is a comparison operator type
 */
export function isComparisonOperatorType(value: unknown): value is ComparisonOperatorType {
  return (
    value === CRITERION_TYPES.EQUALS ||
    value === CRITERION_TYPES.CONTAINS ||
    value === CRITERION_TYPES.GREATER_THAN ||
    value === CRITERION_TYPES.GREATER_EQUAL ||
    value === CRITERION_TYPES.LESS_THAN ||
    value === CRITERION_TYPES.LESS_EQUAL
  );
}

/**
 * Check if a value is a special criterion type
 */
export function isSpecialCriterionType(value: unknown): value is SpecialCriterionType {
  return (
    value === CRITERION_TYPES.CURRENCY_AMOUNT ||
    value === CRITERION_TYPES.ENTITY ||
    value === CRITERION_TYPES.CALENDAR_EVENT
  );
}

/**
 * Check if a value is a static column type
 */
export function isStaticColumnType(value: unknown): value is StaticColumnType {
  return (
    value === CRITERION_TYPES.CONTRACTUAL_PARTY ||
    value === CRITERION_TYPES.MATTER ||
    value === CRITERION_TYPES.OWNER ||
    value === CRITERION_TYPES.DATE
  );
}

/**
 * Get criterion type from an object (checks both 'type' and 'criterion_type' fields)
 * Returns null if not found
 */
export function getCriterionType(obj: unknown): CriterionTypeValue | null {
  if (typeof obj !== 'object' || obj === null) return null;

  // Check 'type' field (internal format)
  if ('type' in obj && typeof obj.type === 'string') {
    const type = obj.type;
    if (isEnumValue(CRITERION_TYPES, type)) {
      return type;
    }
  }

  // Check 'criterion_type' field (API format)
  if ('criterion_type' in obj && typeof obj.criterion_type === 'string') {
    const type = obj.criterion_type;
    if (isEnumValue(CRITERION_TYPES, type)) {
      return type;
    }
  }

  return null;
}

/**
 * Map filter operation strings to criterion types
 * Used to convert operation names from getAvailableFilterOperations to CRITERION_TYPES
 */
export const OPERATION_TO_TYPE_MAP: Record<string, CriterionTypeValue> = {
  equals: CRITERION_TYPES.EQUALS,
  contains: CRITERION_TYPES.CONTAINS,
  greater_than: CRITERION_TYPES.GREATER_THAN,
  greater_equal: CRITERION_TYPES.GREATER_EQUAL,
  less_than: CRITERION_TYPES.LESS_THAN,
  less_equal: CRITERION_TYPES.LESS_EQUAL,
  currency_amount: CRITERION_TYPES.CURRENCY_AMOUNT,
  entity: CRITERION_TYPES.ENTITY,
  calendar_event: CRITERION_TYPES.CALENDAR_EVENT,
  list_any: CRITERION_TYPES.LIST_ANY,
  list_all: CRITERION_TYPES.LIST_ALL,
};
